// src/admin/components/FooterConfig.jsx
import React, { useEffect, useState, useCallback } from "react";
import { ref, get, set } from "firebase/database";
import { db } from "../../lib/firebase";
import "./FooterConfig.css";
import { CONFIG } from "../../config";

export default function FooterConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const [displayLogo, setDisplayLogo] = useState(true);
  const [backgroundcolor, setBackgroundcolor] = useState("#3b2f37");
  const [contactus, setContactus] = useState(true);
  const [aboutus, setAboutus] = useState(true);
  const [socialmedia, setSocialmedia] = useState(true);
  const [locateus, setLocateus] = useState(true);

  // Background image states
  const [backgroundImage, setBackgroundImage] = useState("");
  const [useBackgroundImage, setUseBackgroundImage] = useState(false);
  const [imageBlur, setImageBlur] = useState(0); // 0-20px blur
  const [imageOpacity, setImageOpacity] = useState(1); // 0-1 opacity
  const [imageOverlayColor, setImageOverlayColor] = useState("#000000");
  const [imageOverlayOpacity, setImageOverlayOpacity] = useState(0.5);

  // Form selection
  const [formOptions, setFormOptions] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("");

  /** ---- Config ---- */
  const UPLOAD_URL = CONFIG.UPLOAD_URL;
  const IMAGES_BASE = CONFIG.IMAGES_BASE;

  // Upload image helper
  const uploadImageFile = useCallback(async (file) => {
    if (!file) return null;
    try {
      setUploading(true);
      setError("");
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(UPLOAD_URL, { method: "POST", body: form });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Upload failed (${res.status}) ${text}`);
      }
      const data = await res.json();
      return data?.filename || null;
    } catch (e) {
      console.error(e);
      setError(e.message || "Upload failed");
      return null;
    } finally {
      setUploading(false);
    }
  }, [UPLOAD_URL]);

  // Delete image helper
  const deleteImageFile = useCallback(async (filename) => {
    if (!filename) return false;
    try {
      const res = await fetch(`${IMAGES_BASE}/${encodeURIComponent(filename)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed");
      return true;
    } catch (e) {
      console.error(e);
      setError(e.message || "Delete failed");
      return false;
    }
  }, [IMAGES_BASE]);

  // Handle background image upload
  const handleBackgroundImageUpload = async (file) => {
    if (!file) return;
    const filename = await uploadImageFile(file);
    if (filename) {
      setBackgroundImage(filename);
      setUseBackgroundImage(true);
    }
  };

  // Handle background image delete
  const handleBackgroundImageDelete = async () => {
    if (!backgroundImage) return;
    const ok = await deleteImageFile(backgroundImage);
    if (ok) {
      setBackgroundImage("");
      setUseBackgroundImage(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        // Load footerconfig
        const snap = await get(ref(db, "footerconfig"));
        if (snap.exists()) {
          const v = snap.val();
          setDisplayLogo(Boolean(v.displayLogo));
          setBackgroundcolor(v.backgroundcolor || "#3b2f37");
          setContactus(Boolean(v.contactus));
          setAboutus(Boolean(v.aboutus));
          setSocialmedia(Boolean(v.socialmedia));
          setLocateus(Boolean(v.locateus));
          setSelectedFormId(v.formConfigId || "");
          
          // Load background image settings
          setBackgroundImage(v.backgroundImage || "");
          setUseBackgroundImage(Boolean(v.useBackgroundImage));
          setImageBlur(v.imageBlur || 0);
          setImageOpacity(v.imageOpacity || 1);
          setImageOverlayColor(v.imageOverlayColor || "#000000");
          setImageOverlayOpacity(v.imageOverlayOpacity || 0.5);
        }

        // Load all forms for dropdown
        const formsSnap = await get(ref(db, "formConfigs"));
        if (formsSnap.exists()) {
          const val = formsSnap.val();
          const arr = Object.entries(val).map(([id, data]) => ({
            id,
            name: data.formName || "Untitled form",
          }));
          setFormOptions(arr);
        } else {
          setFormOptions([]);
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load footer configuration.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await set(ref(db, "footerconfig"), {
        displayLogo,
        backgroundcolor,
        contactus,
        aboutus,
        socialmedia,
        locateus,
        formConfigId: selectedFormId || "",
        
        // Background image settings
        backgroundImage,
        useBackgroundImage,
        imageBlur,
        imageOpacity,
        imageOverlayColor,
        imageOverlayOpacity,
      });
    } catch (e) {
      console.error(e);
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="fcfg-loading">Loading…</div>;

  return (
    <section className="fcfg-wrap">
      <h2 className="fcfg-title">Footer configuration</h2>

      <div className="fcfg-card">
        <h3 className="fcfg-subtitle">Visibility</h3>

        <label className="fcfg-check">
          <input
            type="checkbox"
            checked={displayLogo}
            onChange={(e) => setDisplayLogo(e.target.checked)}
          />
          <span>Show logo</span>
        </label>

        <label className="fcfg-check">
          <input
            type="checkbox"
            checked={contactus}
            onChange={(e) => setContactus(e.target.checked)}
          />
          <span>Show "Contact Us"</span>
        </label>

        <label className="fcfg-check">
          <input
            type="checkbox"
            checked={aboutus}
            onChange={(e) => setAboutus(e.target.checked)}
          />
          <span>Show "About Us"</span>
        </label>

        <label className="fcfg-check">
          <input
            type="checkbox"
            checked={socialmedia}
            onChange={(e) => setSocialmedia(e.target.checked)}
          />
          <span>Show Social Media</span>
        </label>

        <label className="fcfg-check">
          <input
            type="checkbox"
            checked={locateus}
            onChange={(e) => setLocateus(e.target.checked)}
          />
          <span>Show "Locate Us"</span>
        </label>
      </div>

      {/* Footer form selection */}
      <div className="fcfg-card">
        <h3 className="fcfg-subtitle">Footer Enquiry Form</h3>
        <p className="fcfg-help">
          Choose a form from <strong>Form Config</strong> to display in the
          footer, or select "No form" to hide it.
        </p>

        <div className="fcfg-row">
          <div className="fcfg-col">
            <label className="fcfg-label">Select form</label>
            <select
              value={selectedFormId}
              onChange={(e) => setSelectedFormId(e.target.value)}
            >
              <option value="">No form</option>
              {formOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {formOptions.length === 0 && (
          <p className="fcfg-help small">
            No forms found. Create forms in the <strong>Form Config</strong> tab.
          </p>
        )}
      </div>

      {/* Background Settings */}
      <div className="fcfg-card">
        <h3 className="fcfg-subtitle">Background Settings</h3>
        
        {/* Use Background Image Toggle */}
        <label className="fcfg-check">
          <input
            type="checkbox"
            checked={useBackgroundImage}
            onChange={(e) => setUseBackgroundImage(e.target.checked)}
          />
          <span>Use Background Image (instead of solid color)</span>
        </label>

        {/* Background Color (when not using image) */}
        <div style={{ 
          opacity: useBackgroundImage ? 0.6 : 1,
          pointerEvents: useBackgroundImage ? "none" : "auto",
          marginTop: "15px"
        }}>
          <label className="fcfg-label">Background Color</label>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <input
              type="color"
              value={backgroundcolor}
              onChange={(e) => setBackgroundcolor(e.target.value)}
              style={{ width: "40px", height: "40px" }}
            />
            <input
              value={backgroundcolor}
              onChange={(e) => setBackgroundcolor(e.target.value)}
              placeholder="#3b2f37"
              style={{ flex: 1, padding: "8px" }}
            />
          </div>
        </div>

        {/* Background Image Upload */}
        <div style={{ 
          opacity: useBackgroundImage ? 1 : 0.6,
          pointerEvents: useBackgroundImage ? "auto" : "none",
          marginTop: "20px",
          paddingTop: "20px",
          borderTop: "1px solid #eee"
        }}>
          <h4 style={{ marginBottom: "10px" }}>Background Image</h4>
          
          {backgroundImage ? (
            <div style={{ marginBottom: "15px" }}>
              <img
                src={`${IMAGES_BASE}/${encodeURIComponent(backgroundImage)}`}
                alt="Background preview"
                style={{ 
                  width: "100%", 
                  maxHeight: "200px", 
                  objectFit: "cover",
                  borderRadius: "4px",
                  filter: `blur(${imageBlur}px)`,
                  opacity: imageOpacity
                }}
              />
              <div className="fcfg-actions-inline" style={{ marginTop: "10px" }}>
                <label className="fcfg-btn" style={{
                  padding: "8px 16px",
                  background: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  display: "inline-block"
                }}>
                  Change Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleBackgroundImageUpload(e.target.files?.[0])}
                    style={{ display: "none" }}
                  />
                </label>
                <button
                  className="fcfg-btn danger"
                  onClick={handleBackgroundImageDelete}
                  style={{
                    padding: "8px 16px",
                    background: "#f44336",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    marginLeft: "10px"
                  }}
                >
                  Delete Image
                </button>
              </div>
            </div>
          ) : (
            <div style={{ 
              padding: "20px", 
              textAlign: "center", 
              background: "#f5f5f5", 
              borderRadius: "4px",
              marginBottom: "15px"
            }}>
              <p style={{ color: "#666", marginBottom: "10px" }}>
                No background image selected
              </p>
              <label className="fcfg-btn" style={{
                padding: "8px 16px",
                background: "#2196F3",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                display: "inline-block"
              }}>
                Upload Background Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleBackgroundImageUpload(e.target.files?.[0])}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          )}

          {/* Image Effects Controls */}
          <div className="fcfg-row">
            <div className="fcfg-col">
              <label className="fcfg-label">Image Blur</label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="range"
                  min="0"
                  max="20"
                  step="1"
                  value={imageBlur}
                  onChange={(e) => setImageBlur(parseInt(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span style={{ minWidth: "30px", textAlign: "center" }}>
                  {imageBlur}px
                </span>
              </div>
              <small className="fcfg-help">Adjust blur effect on background image</small>
            </div>

            <div className="fcfg-col">
              <label className="fcfg-label">Image Opacity</label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={imageOpacity}
                  onChange={(e) => setImageOpacity(parseFloat(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span style={{ minWidth: "30px", textAlign: "center" }}>
                  {imageOpacity.toFixed(1)}
                </span>
              </div>
              <small className="fcfg-help">Adjust transparency of background image</small>
            </div>
          </div>

          {/* Image Overlay Controls */}
          <div className="fcfg-row" style={{ marginTop: "20px" }}>
            <div className="fcfg-col">
              <label className="fcfg-label">Overlay Color</label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="color"
                  value={imageOverlayColor}
                  onChange={(e) => setImageOverlayColor(e.target.value)}
                  style={{ width: "40px", height: "40px" }}
                />
                <input
                  type="text"
                  value={imageOverlayColor}
                  onChange={(e) => setImageOverlayColor(e.target.value)}
                  placeholder="#000000"
                  style={{ flex: 1, padding: "8px" }}
                />
              </div>
              <small className="fcfg-help">Color overlay on top of background image</small>
            </div>

            <div className="fcfg-col">
              <label className="fcfg-label">Overlay Opacity</label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={imageOverlayOpacity}
                  onChange={(e) => setImageOverlayOpacity(parseFloat(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span style={{ minWidth: "30px", textAlign: "center" }}>
                  {imageOverlayOpacity.toFixed(1)}
                </span>
              </div>
              <small className="fcfg-help">Adjust overlay transparency</small>
            </div>
          </div>

          {/* Preview Note */}
          {backgroundImage && (
            <div style={{ 
              backgroundColor: "#e3f2fd", 
              padding: "10px", 
              borderRadius: "4px",
              marginTop: "15px",
              fontSize: "14px"
            }}>
              <strong>Preview:</strong> Background will have {imageBlur}px blur, {imageOpacity.toFixed(1)} opacity, 
              with {imageOverlayColor} overlay at {imageOverlayOpacity.toFixed(1)} opacity.
            </div>
          )}
        </div>
      </div>

      {uploading && <p className="fcfg-info">Uploading image…</p>}
      {error && <p className="fcfg-error">{error}</p>}
      <div className="fcfg-actions">
        <button className="fcfg-btn primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Footer Config"}
        </button>
      </div>
    </section>
  );
}
